import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { 
  RefreshCw, 
  Search, 
  AlertTriangle, 
  CheckCircle,
  Filter,
  Wallet,
  DollarSign,
  ArrowDownCircle,
  Send,
  Clock
} from 'lucide-react';
import { toast } from 'sonner';
import { formatCurrency } from '../lib/formatters';
import { PayablesPanel } from './syndication/SyndicationPayables';  // 11/03 Josh: payables on the same screen

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatDate = (dateString) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const SyndicationDashboard = () => {
  const { getAuthHeaders, user } = useAuth();
  const [dashboardData, setDashboardData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [recomputing, setRecomputing] = useState(false);
  
  // Filters - always LIVE (validation mode removed)
  const tenant = 'live';
  const [filterAvailable, setFilterAvailable] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Withdrawal request dialog
  const [withdrawalDialogOpen, setWithdrawalDialogOpen] = useState(false);
  const [selectedSyndicator, setSelectedSyndicator] = useState(null);
  const [withdrawalAmount, setWithdrawalAmount] = useState('');
  const [withdrawalNote, setWithdrawalNote] = useState('');
  const [submittingRequest, setSubmittingRequest] = useState(false);
  
  const isAdmin = user?.roles?.includes('admin');
  const isSyndicator = user?.roles?.includes('syndicator');

  const fetchDashboardData = useCallback(async () => {
    setLoading(true);
    try {
      const headers = {
        ...getAuthHeaders(),
        'X-Tenant': tenant
      };
      
      const params = new URLSearchParams();
      if (filterAvailable) {
        params.append('available_gt', '0');
      }
      if (searchTerm) {
        params.append('search', searchTerm);
      }
      
      const response = await axios.get(
        `${API}/syndications/dashboard?${params.toString()}`,
        { headers }
      );
      setDashboardData(response.data);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
      toast.error('Failed to load syndication dashboard');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, tenant, filterAvailable, searchTerm]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  const handleRecomputeStale = async () => {
    if (!isAdmin) {
      toast.error('Admin access required');
      return;
    }
    
    setRecomputing(true);
    try {
      const response = await axios.post(
        `${API}/syndications/recompute/stale`,
        {},
        { 
          headers: {
            ...getAuthHeaders(),
            'X-Tenant': tenant
          }
        }
      );
      toast.success(`Recomputed ${response.data.stale_deals_processed} stale deals`);
      fetchDashboardData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to recompute stale syndications');
    } finally {
      setRecomputing(false);
    }
  };

  // Open withdrawal request dialog
  const handleOpenWithdrawalDialog = (syndicator) => {
    // Only allow in LIVE tenant
    if (tenant !== 'live') {
      toast.error('Withdrawal requests are only allowed in LIVE mode');
      return;
    }
    
    // Check if user can request for this syndicator
    if (!isAdmin && user?.contact_id !== syndicator.syndicator_id) {
      toast.error('You can only request withdrawals for your own account');
      return;
    }
    
    if (syndicator.available_total <= 0) {
      toast.error('No available balance to withdraw');
      return;
    }
    
    setSelectedSyndicator(syndicator);
    setWithdrawalAmount(syndicator.available_total.toFixed(2));
    setWithdrawalNote('');
    setWithdrawalDialogOpen(true);
  };

  // Submit withdrawal request
  const handleSubmitWithdrawalRequest = async () => {
    if (!selectedSyndicator) return;
    
    const amount = parseFloat(withdrawalAmount);
    if (isNaN(amount) || amount <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    
    if (amount > selectedSyndicator.available_total) {
      toast.error(`Amount cannot exceed available balance of ${formatCurrency(selectedSyndicator.available_total)}`);
      return;
    }
    
    setSubmittingRequest(true);
    try {
      await axios.post(
        `${API}/withdrawal-requests`,
        {
          syndicator_id: selectedSyndicator.syndicator_id,
          amount: amount,
          note: withdrawalNote || null,
          attachment_urls: []
        },
        { 
          headers: {
            ...getAuthHeaders(),
            'X-Tenant': 'live'
          }
        }
      );
      
      toast.success(`Withdrawal request for ${formatCurrency(amount)} submitted successfully`);
      setWithdrawalDialogOpen(false);
      setSelectedSyndicator(null);
      fetchDashboardData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit withdrawal request');
    } finally {
      setSubmittingRequest(false);
    }
  };

  // Calculate totals
  const totals = dashboardData.reduce(
    (acc, row) => ({
      principalInvested: acc.principalInvested + (row.principal_invested || 0),
      availableFees: acc.availableFees + (row.available_fees || 0),
      availableTotal: acc.availableTotal + (row.available_total || 0),
      withdrawn: acc.withdrawn + (row.withdrawn_total || 0),
      remainingOwed: acc.remainingOwed + (row.remaining_owed || 0)
    }),
    { principalInvested: 0, availableFees: 0, availableTotal: 0, withdrawn: 0, remainingOwed: 0 }
  );

  const hasStaleData = dashboardData.some(row => row.is_stale);

  // Check if user can see request withdrawal button
  const canRequestWithdrawal = (syndicator) => {
    if (tenant !== 'live') return false;
    if (syndicator.available_total <= 0) return false;
    if (isAdmin) return true;
    if (isSyndicator && user?.contact_id === syndicator.syndicator_id) return true;
    return false;
  };

  return (
    <Layout>
      <div className="px-4 sm:px-0 space-y-6" data-testid="syndication-dashboard">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Syndication Dashboard</h1>
            <p className="text-sm text-gray-500">
              Distribution overview for all syndicators
            </p>
          </div>
          
          {/* Controls */}
          <div className="flex flex-wrap items-center gap-4">
            {/* LIVE Tenant Badge */}
            <div className="flex items-center gap-2 bg-green-50 px-3 py-2 rounded-lg">
              <span className="text-sm font-medium text-green-600">LIVE</span>
            </div>
            
            {/* Recompute Stale Button */}
            {isAdmin && hasStaleData && (
              <Button
                onClick={handleRecomputeStale}
                disabled={recomputing}
                variant="outline"
                className="text-amber-600 border-amber-300 hover:bg-amber-50"
                data-testid="recompute-stale-btn"
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${recomputing ? 'animate-spin' : ''}`} />
                Recompute Stale
              </Button>
            )}
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <Link to="/syndicated-deals" className="block cursor-pointer group" data-testid="available-base-card-link">
            <Card className="h-full transition-all duration-200 hover:shadow-md hover:border-blue-300 group-hover:bg-blue-50/50 cursor-pointer">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium text-gray-600">Principal Invested</CardTitle>
                <Wallet className="h-4 w-4 text-blue-600" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-blue-600">{formatCurrency(totals.principalInvested)}</div>
              </CardContent>
            </Card>
          </Link>
          
          <Link to="/syndicated-deals" className="block cursor-pointer group" data-testid="available-fees-card-link">
            <Card className="h-full transition-all duration-200 hover:shadow-md hover:border-purple-300 group-hover:bg-purple-50/50 cursor-pointer">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium text-gray-600">Available Fees</CardTitle>
                <DollarSign className="h-4 w-4 text-purple-600" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-purple-600">{formatCurrency(totals.availableFees)}</div>
              </CardContent>
            </Card>
          </Link>
          
          <Link to="/syndicated-deals" className="block cursor-pointer group" data-testid="available-total-card-link">
            <Card className="h-full transition-all duration-200 hover:shadow-md hover:border-green-300 group-hover:bg-green-50/50 cursor-pointer">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium text-gray-600">Available Balance</CardTitle>
                <CheckCircle className="h-4 w-4 text-green-600" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-green-600">{formatCurrency(totals.availableTotal)}</div>
              </CardContent>
            </Card>
          </Link>
          
          <Link to="/syndication-withdrawals" className="block cursor-pointer group" data-testid="withdrawn-total-card-link">
            <Card className="h-full transition-all duration-200 hover:shadow-md hover:border-orange-300 group-hover:bg-orange-50/50 cursor-pointer">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium text-gray-600">Total Withdrawn</CardTitle>
                <ArrowDownCircle className="h-4 w-4 text-orange-600" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-orange-600">{formatCurrency(totals.withdrawn)}</div>
              </CardContent>
            </Card>
          </Link>
          
          <Link to="/syndicated-deals" className="block cursor-pointer group" data-testid="remaining-owed-card-link">
            <Card className="h-full transition-all duration-200 hover:shadow-md hover:border-red-300 group-hover:bg-red-50/50 cursor-pointer">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium text-gray-600">RTR Remaining</CardTitle>
                <Clock className="h-4 w-4 text-red-600" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-red-600">{formatCurrency(totals.remainingOwed)}</div>
              </CardContent>
            </Card>
          </Link>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="py-4">
            <div className="flex flex-wrap items-center gap-4">
              {/* Search */}
              <div className="relative flex-1 min-w-[200px] max-w-sm">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  type="text"
                  placeholder="Search by syndicator name..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9"
                  data-testid="search-input"
                />
              </div>
              
              {/* Filter Available > 0 */}
              <div className="flex items-center gap-2">
                <Filter className="h-4 w-4 text-gray-500" />
                <Label htmlFor="filter-available" className="text-sm">Available &gt; $0</Label>
                <Switch
                  id="filter-available"
                  checked={filterAvailable}
                  onCheckedChange={setFilterAvailable}
                  data-testid="filter-available-toggle"
                />
              </div>
              
              {/* Refresh */}
              <Button
                variant="outline"
                size="sm"
                onClick={fetchDashboardData}
                disabled={loading}
                data-testid="refresh-btn"
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Stale Warning Banner */}
        {hasStaleData && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <AlertTriangle className="h-5 w-5 text-amber-600" />
              <div>
                <p className="font-medium text-amber-800">Stale Data Detected</p>
                <p className="text-sm text-amber-600">
                  Some syndications have outdated calculations.
                </p>
              </div>
            </div>
            <Button
              onClick={handleRecomputeStale}
              disabled={recomputing}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="recompute-stale-banner-btn"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${recomputing ? 'animate-spin' : ''}`} />
              {recomputing ? 'Recomputing...' : 'Recompute Now'}
            </Button>
          </div>
        )}

        {/* Data Table */}
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="text-center py-12">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            ) : dashboardData.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                No syndication data found.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table data-testid="syndication-dashboard-table">
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead>Syndicator</TableHead>
                      <TableHead className="text-right">Principal Invested</TableHead>
                      <TableHead className="text-right">Net Principal</TableHead>
                      <TableHead className="text-right">Available Balance</TableHead>
                      <TableHead className="text-right">Available Fees</TableHead>
                      <TableHead className="text-right font-semibold">Available Total</TableHead>
                      <TableHead className="text-right">Total Withdrawn</TableHead>
                      <TableHead className="text-right">RTR Remaining</TableHead>
                      <TableHead>Last Computed</TableHead>
                      <TableHead className="text-center">Status</TableHead>
                      {tenant === 'live' && <TableHead className="text-center">Actions</TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {dashboardData.map((row) => (
                      <TableRow 
                        key={row.syndicator_id}
                        className={row.is_stale ? 'bg-amber-50' : ''}
                        data-testid={`syndicator-row-${row.syndicator_id}`}
                      >
                        <TableCell className="font-medium">
                          <div>
                            <a
                              href={`/syndications/syndicator/${row.syndicator_id}`}
                              className="text-blue-600 hover:underline cursor-pointer"
                              onClick={e => { e.preventDefault(); e.stopPropagation(); window.location.href = `/syndications/syndicator/${row.syndicator_id}`; }}
                              data-testid={`syndicator-link-${row.syndicator_id}`}
                            >
                              {row.syndicator_name}
                            </a>
                            <span className="text-xs text-gray-400 ml-2">
                              ({row.deal_count} deals)
                            </span>
                          </div>
                        </TableCell>
                        <TableCell className="text-right text-blue-600">
                          {formatCurrency(row.principal_invested)}
                        </TableCell>
                        <TableCell className={`text-right ${(row.net_principal || 0) < 0 ? 'text-green-600' : 'text-gray-700'}`}>
                          {formatCurrency(row.net_principal)}
                        </TableCell>
                        <TableCell className="text-right text-gray-700">
                          {formatCurrency(row.available_balance)}
                        </TableCell>
                        <TableCell className="text-right text-purple-600">
                          {formatCurrency(row.available_fees)}
                        </TableCell>
                        <TableCell className="text-right font-semibold text-green-600">
                          {formatCurrency(row.available_total)}
                        </TableCell>
                        <TableCell className="text-right text-orange-600">
                          {formatCurrency(row.withdrawn_total)}
                        </TableCell>
                        <TableCell className="text-right text-red-600">
                          {formatCurrency(row.remaining_owed)}
                        </TableCell>
                        <TableCell className="text-sm text-gray-500">
                          {formatDate(row.last_computed)}
                        </TableCell>
                        <TableCell className="text-center">
                          {row.is_stale ? (
                            <Badge variant="outline" className="bg-amber-100 text-amber-700 border-amber-300">
                              Stale
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="bg-green-100 text-green-700 border-green-300">
                              Current
                            </Badge>
                          )}
                        </TableCell>
                        {tenant === 'live' && (
                          <TableCell className="text-center">
                            {/* 11/03 (Josh): admin = "Withdraw" (pay out with a reason, saved to the profile) → the
                                checkout cart; a syndicator login still "Request"s. */}
                            {canRequestWithdrawal(row) && (isAdmin ? (
                              <Link to={`/syndications/disbursements?syndicator=${row.syndicator_id}`}>
                                <Button size="sm" variant="outline" className="text-green-600 border-green-300 hover:bg-green-50" data-testid={`withdraw-${row.syndicator_id}`}>
                                  <Send className="h-3 w-3 mr-1" />
                                  Withdraw
                                </Button>
                              </Link>
                            ) : (
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-green-600 border-green-300 hover:bg-green-50"
                                onClick={() => handleOpenWithdrawalDialog(row)}
                                data-testid={`request-withdrawal-${row.syndicator_id}`}
                              >
                                <Send className="h-3 w-3 mr-1" />
                                Request
                              </Button>
                            ))}
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {isAdmin && <PayablesPanel embedded />}

        {/* Withdrawal Request Dialog */}
        <Dialog open={withdrawalDialogOpen} onOpenChange={setWithdrawalDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Request Withdrawal</DialogTitle>
              <DialogDescription>
                Request a withdrawal for {selectedSyndicator?.syndicator_name}
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              {/* Available Balance Info */}
              <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                <p className="text-sm text-green-700">
                  Available Balance: <span className="font-bold">{formatCurrency(selectedSyndicator?.available_total || 0)}</span>
                </p>
              </div>
              
              {/* Amount Input */}
              <div>
                <Label htmlFor="withdrawal-amount">Amount *</Label>
                <div className="relative mt-1">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                  <Input
                    id="withdrawal-amount"
                    type="number"
                    step="0.01"
                    min="0.01"
                    max={selectedSyndicator?.available_total || 0}
                    value={withdrawalAmount}
                    onChange={(e) => setWithdrawalAmount(e.target.value)}
                    className="pl-7"
                    placeholder="0.00"
                    data-testid="withdrawal-amount-input"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Maximum: {formatCurrency(selectedSyndicator?.available_total || 0)}
                </p>
              </div>
              
              {/* Note Input */}
              <div>
                <Label htmlFor="withdrawal-note">Note (Optional)</Label>
                <Textarea
                  id="withdrawal-note"
                  value={withdrawalNote}
                  onChange={(e) => setWithdrawalNote(e.target.value)}
                  placeholder="Add a note or reason for this withdrawal request..."
                  className="mt-1"
                  rows={3}
                  maxLength={500}
                  data-testid="withdrawal-note-input"
                />
                <p className="text-xs text-gray-500 mt-1">
                  {withdrawalNote.length}/500 characters
                </p>
              </div>
            </div>
            
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setWithdrawalDialogOpen(false)}
                disabled={submittingRequest}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSubmitWithdrawalRequest}
                disabled={submittingRequest || !withdrawalAmount || parseFloat(withdrawalAmount) <= 0}
                className="bg-green-600 hover:bg-green-700"
                data-testid="submit-withdrawal-btn"
              >
                {submittingRequest ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4 mr-2" />
                    Submit Request
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default SyndicationDashboard;
